import { useCallback } from 'react'

import { useApolloClient } from '@apollo/client'
import {
  UpdateEmployeeAttendableDocument,
  UpdateEmployeeAttendableMutation,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'

interface AttendableChange {
  employeeId: number
  attendable: boolean
}

export const useBulkAttendableUpdate = () => {
  const client = useApolloClient()
  const [updateEmployeeAttendable] = useMutation(UpdateEmployeeAttendableDocument, {
    extractorFn: (data: UpdateEmployeeAttendableMutation) =>
      data.employeesMutations.updateEmployeesEmployee,
  })

  const bulkUpdateAttendable = useCallback(
    async (changes: AttendableChange[]) => {
      const results = await Promise.all(
        changes.map((change) =>
          updateEmployeeAttendable({
            variables: {
              id: change.employeeId,
              attendable: change.attendable,
            },
          })
        )
      )

      const handled = results.map((r) => handleResult(r, () => {}))

      const successfulCount = handled.filter((r) => r.success).length
      const errors = handled.filter((r) => !r.success).flatMap((r) => (r.success ? [] : r.errors))

      if (successfulCount > 0) {
        const includeChanges = changes.filter((change) => change.attendable)
        const excludeChanges = changes.filter((change) => !change.attendable)

        if (includeChanges.length > 0) {
          const trackingData = {
            employee_ids: includeChanges.map((c) => c.employeeId.toString()),
            employee_count: includeChanges.length,
          }
          track(trackingEvents.timeTracking.bulkIncludeEmployees, trackingData)
        }

        if (excludeChanges.length > 0) {
          const trackingData = {
            employee_ids: excludeChanges.map((c) => c.employeeId.toString()),
            employee_count: excludeChanges.length,
          }
          track(trackingEvents.timeTracking.bulkExcludeEmployees, trackingData)
        }
      }

      await client.refetchQueries({ include: ['AttendanceCustomPeriodEmployeesCollectionList'] })

      return {
        successful: successfulCount,
        failed: handled.length - successfulCount,
        errors,
      }
    },
    [updateEmployeeAttendable, client]
  )

  return { bulkUpdateAttendable }
}
